// Route clearance: where things move on the map, as they really move. Every moving line of a resort as it is driven
// or walked (`moverLanes`: each ride through rides.js driveLine, so an open line's end turns and a snowmobile's balloon
// are in it; karts round their track with its banks; walkers along a line; sledders down a hill and back up beside
// it; a walk view's path), each with the half width its mover sweeps (`body`) and that plus room for a trunk and the
// crown low over it (`half`). main.js hides every tree inside a lane (`laneMask`, every map, every season's lines at
// once: trees are built once); tools/route_clearance.mjs and tests/unit/runtime/route-clearance.test.mjs list what
// else stands in one (lines/clearance.js laneHits: buildings, fences, props, open water) for the owner to redraw: a
// line is never moved or reshaped automatically. Runs once per configuration, never per frame. No DOM.
// Not here: `life.between` walkers (they go along the map's own ways, whose trees vegetation.py clears) and the
// wander / herd areas (main.js LZ.roamArea keeps them off trunks and fences).
import { driveLine, packageRoute } from './rides.js';
import { lengthOf, nearAny } from './lines/measure.js';

/** m a mover sweeps either side of its line: half its widest part, with a towed sleigh's swing inside a turn
 *  (`sleigh`: an animal sleigh line), a rider's knees, a walker keeping 0.5 m right (lazy/people.js WAY.side), a
 *  sledder walking back up 1.5 m beside the hill's line (SLED_BEAT.lane), a walk view's eye. */
export const BODY = Object.freeze({ sleigh: 0.7, dogsled: 0.6, horsesleigh: 0.8, horseback: 0.6, snowmobile: 0.7, walk: 0.8, sled: 1.9, view: 0.4 });
/** m cleared beyond a vehicle's body for a trunk and its low crown (lazy/touches.js clears an edited ride's trail by it too). */
export const LANE_MARGIN = 1.2;
/** The same for people on foot (they brush past a tree a sleigh would hit). */
export const FOOT_MARGIN = 0.8;

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const arr = (v) => (Array.isArray(v) ? v : []);
const xy = (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]);
/** The body kind of a ride entry (rides.js teams.kind; none = an animal sleigh line). */
export const rideKind = (cfg) => (Object.hasOwn(BODY, cfg?.teams?.kind) ? cfg.teams.kind : 'sleigh');

/**
 * The line a walk view (`resort.walkViews.<place>.path`) walks, as main.js walkPath names it: a drawn path's id
 * (`drawn`: the config's drawnRoads), a way id `<road|path|trail>:<name>[#n]` or a way's name (its longest piece) of
 * `scene` (the map's ways as touched). Returns { pts, closed } or null.
 */
export function wayRef(ref, scene = {}, drawn = []) {
  const s = String(ref || ''), m = /^(road|path|trail):(.+?)(?:#(\d+))?$/.exec(s);
  const ways = m ? arr(scene?.[`${m[1]}s`]).filter((w) => w?.name === m[2]).filter((_, i) => !m[3] || i === m[3] - 1) : [...arr(scene?.roads), ...arr(scene?.paths), ...arr(scene?.trails)].filter((w) => w?.name === s);
  const lines = [...arr(drawn).filter((w) => w?.id === s), ...ways].map((w) => w?.pts).filter((p) => Array.isArray(p) && p.length > 1 && p.every(xy));
  const pts = lines.sort((a, b) => lengthOf(b) - lengthOf(a))[0];
  return pts ? { pts, closed: pts.length > 3 && Math.hypot(pts[0][0] - pts.at(-1)[0], pts[0][1] - pts.at(-1)[1]) < 0.05 } : null;
}

/**
 * Every moving line of `configs` (resort configs: the base and every season's, touches laid in: an edited route is a
 * ride's `routes.__touched`, a ride added on a path is a `rides[]` entry) as its mover goes, once each:
 * [{ id, kind, pts (a loop ends on its first point again), half (trees), body (hard obstacles), seasons (null = all) }].
 * `scene` = scene.json as touched (a walk view's way). The ride's line is driveLine's polyline before samplePath rounds
 * its corners (by ~`smooth` m, well inside the margin: the package test checks the sampled line stays in the lane).
 */
export function moverLanes(configs, scene = {}) {
  const out = [], seen = new Set();
  const add = (id, kind, pts, body, margin, closed, seasons) => {
    const P0 = arr(pts).filter(xy);
    if (P0.length < 2 || !(body > 0)) return;
    const P = closed && P0.length > 2 && (P0[0][0] !== P0.at(-1)[0] || P0[0][1] !== P0.at(-1)[1]) ? [...P0, P0[0]] : P0;
    const key = `${kind}|${id}|${body}|${P.length}|${P[0]}|${P[P.length >> 1]}|${P.at(-1)}`; // (the same line in several season configs: once)
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ id: String(id ?? kind), kind, pts: P, half: body + margin, body, seasons: Array.isArray(seasons) ? seasons : null });
  };
  for (const r of arr(configs)) {
    if (!isObj(r)) continue;
    for (const cfg of arr(r.rides)) {
      const route = isObj(cfg) && packageRoute(cfg);
      if (!route) continue;
      const k = rideKind(cfg), L = driveLine(cfg, route); // (rides.js createRides drives exactly this line)
      add(cfg.id, k, L.path, BODY[k], LANE_MARGIN, L.closed !== false, cfg.seasons);
      for (const r of arr(cfg.rounds)) if (isObj(r) && arr(r.path).length > 1) { const Q = driveLine(cfg, { path: r.path, closed: r.closed !== false }); add(cfg.id, k, Q.path, BODY[k], LANE_MARGIN, Q.closed !== false, cfg.seasons); } // its further rounds (rides.js roundsOf; the sense does not change the lane)
    }
    for (const t of arr(r.tracks)) if (isObj(t) && arr(t.centerline).length > 2) add(t.id, 'kart', t.centerline, (t.width ?? 7) / 2 + (t.banks?.width ?? 1.6), 0.5, t.closed !== false, t.seasons); // (karts stay on their track: its ice and banks)
    const life = isObj(r.life) ? r.life : {};
    for (const w of arr(life.walk)) if (isObj(w) && !w.off) add(w.id, 'walk', w.pts, BODY.walk, FOOT_MARGIN, !!w.closed, isObj(w.as) ? Object.keys(w.as).filter((s) => w.as[s]) : null);
    for (const s of arr(life.sled)) if (isObj(s) && !s.off) add(s.id, 'sled', s.line, BODY.sled, FOOT_MARGIN, false, s.seasons === null ? null : s.seasons ?? ['winter']);
    for (const [place, v] of Object.entries(isObj(r.walkViews) ? r.walkViews : {})) {
      const L = isObj(v) && wayRef(v.path, scene, r.drawnRoads);
      if (L) add(place, 'view', L.pts, BODY.view, FOOT_MARGIN, L.closed, v.seasons);
    }
  }
  return out;
}

/** (x, z) -> whether a tree there stands in a lane (lines/measure.js nearAny, 4 m cells); null for no lanes. */
export function laneMask(lanes) {
  if (!lanes?.length) return null;
  const near = nearAny(lanes, (l) => l.half, 4), p = [0, 0];
  return (x, z) => { p[0] = x; p[1] = z; return near(p); };
}
